import {
  computeGrossAssets,
  computeNetWorth,
  NetWorthSnapshot,
  newSnapshotId,
} from '../models/NetWorthTypes';

const CSV_HEADERS = [
  'recordDate',
  'outstanding',
  'savings',
  'equityAmountInvested',
  'equityCurrentValue',
  'mfAmountInvested',
  'mfCurrentValue',
  'corporateFdBonds',
  'ppf',
  'europeIncome',
  'euSalary',
  'euEquity',
  'euConversionRate',
  'salary',
  'bankFd',
  'cryptoUsdt',
  'cryptoUsd',
  'cryptoToInrRate',
  'realEstate',
  'realEstateDescription',
  'goldGrams',
  'goldInrPerGram',
  'goldTotalInr',
  'usStockUsd',
  'usStockConversionRate',
  'usStockTotalInr',
  'usAssetsTotalInr',
  'usdAmount',
  'usdToInrRate',
  'excelTotalAssets',
  'customerFdLiability',
  'partnerCapitalLiability',
  'monthlyGrowthRatePct',
  'monthlyInterestIncomePossible',
  'monthlyInterestIncome',
  'monthOnMonthChangePct',
  'description',
  'sloganOfTheMonth',
] as const;

function escapeCsvCell(v: string): string {
  if (/[",\n\r]/.test(v)) return `"${v.replace(/"/g, '""')}"`;
  return v;
}

function parseNum(s: string): number | undefined {
  const t = s.replace(/,/g, '').trim();
  if (!t) return undefined;
  const n = parseFloat(t);
  return Number.isFinite(n) ? n : undefined;
}

/** Parse DD/MMM/YYYY, DD/MM/YYYY, DD-Mon-YYYY, ISO */
export function parseFlexibleDate(s: string): string | null {
  const t = s.trim();
  if (!t) return null;
  const iso = /^\d{4}-\d{2}-\d{2}$/.exec(t);
  if (iso) return t;
  const m1 = /^(\d{1,2})[-/](\d{1,2})[-/](\d{4})$/.exec(t);
  if (m1) {
    const d = parseInt(m1[1], 10);
    const mo = parseInt(m1[2], 10);
    const y = parseInt(m1[3], 10);
    if (mo >= 1 && mo <= 12 && d >= 1 && d <= 31) {
      return `${y}-${String(mo).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
    }
  }
  const m2 = /^(\d{1,2})\/([A-Za-z]{3,})\/(\d{4})$/.exec(t);
  if (m2) {
    const d = parseInt(m2[1], 10);
    const y = parseInt(m2[3], 10);
    const mon = new Date(`${m2[2]} 1, 2000`).getMonth();
    if (!Number.isNaN(mon)) {
      return `${y}-${String(mon + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
    }
  }
  const tryDate = new Date(t);
  if (!Number.isNaN(tryDate.getTime())) {
    return tryDate.toISOString().slice(0, 10);
  }
  return null;
}

/** Map loose header names from Excel exports */
function normalizeHeader(h: string): string {
  const x = h
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .replace(/[()]/g, '');
  const map: Record<string, string> = {
    'record date': 'recordDate',
    'recorddate': 'recordDate',
    date: 'recordDate',
    outstanding: 'outstanding',
    savings: 'savings',
    equity: 'equityCurrentValue',
    'equity current value': 'equityCurrentValue',
    'equity amount invested': 'equityAmountInvested',
    'amount invested': 'equityAmountInvested',
    'current value': 'equityCurrentValue',
    'mutual funds/sip': 'mfCurrentValue',
    'mf current value': 'mfCurrentValue',
    'mf amount invested': 'mfAmountInvested',
    sip: 'mfCurrentValue',
    'corporate fd/bonds': 'corporateFdBonds',
    'corporate fd': 'corporateFdBonds',
    ppf: 'ppf',
    'europe income': 'europeIncome',
    salary: 'salary',
    'bank fd': 'bankFd',
    'crypto(ustd)': 'cryptoUsdt',
    crypto: 'cryptoUsdt',
    'real estate': 'realEstate',
    stock: 'usStockUsd',
    usstockusd: 'usStockUsd',
    conversionrate: 'usStockConversionRate',
    'us stock total': 'usStockTotalInr',
    usstocktotalinr: 'usStockTotalInr',
    description: 'description',
    'monthly growth rate': 'monthlyGrowthRatePct',
    'monthly interest income possibility': 'monthlyInterestIncomePossible',
    'monthly interest income': 'monthlyInterestIncome',
    'month on month change': 'monthOnMonthChangePct',
    'mom change': 'monthOnMonthChangePct',
    'customer fd (liability)': 'customerFdLiability',
    'customer fd liability': 'customerFdLiability',
    'partner capital': 'partnerCapitalLiability',
    'partner capital liability': 'partnerCapitalLiability',
    'partner capital (held in trust)': 'partnerCapitalLiability',
    'slogan of the month': 'sloganOfTheMonth',
    slogan: 'sloganOfTheMonth',
    bonds: 'corporateFdBonds',
    fd: 'bankFd',
    'eu salary': 'euSalary',
    'eu equity': 'euEquity',
    'eu conversion rate': 'euConversionRate',
    'crypto usd': 'cryptoUsd',
    'crypto to inr rate': 'cryptoToInrRate',
    'real estate values': 'realEstate',
    'gold conversion rate': 'goldInrPerGram',
    'us stocks dollar': 'usStockUsd',
    'us conversion rate': 'usStockConversionRate',
    mf_invested: 'mfAmountInvested',
    mf_current_value: 'mfCurrentValue',
    equity_amount_invested: 'equityAmountInvested',
    equity_current_value: 'equityCurrentValue',
    'record datedd/mm/yyyy': 'recordDate',
    'record dateddmmyyyy': 'recordDate',
    total: 'excelTotalAssets',
    'gold(grams)': 'goldGrams',
    'gold grams': 'goldGrams',
    'conversion rate/gram': 'goldInrPerGram',
    'conversion rate': 'goldInrPerGram',
    'total amount': 'goldTotalInr',
    'assets us': 'usAssetsTotalInr',
    usd: 'usdAmount',
    gold: 'goldGrams',
  };
  return map[x] || h.trim();
}

export function snapshotsToCsv(rows: NetWorthSnapshot[]): string {
  const lines: string[] = [];
  lines.push(
    [
      ...CSV_HEADERS,
      'computedGrossAssets',
      'computedNetWorth',
    ].join(',')
  );
  for (const s of rows) {
    const cells = CSV_HEADERS.map((key) => {
      const v = s[key as keyof NetWorthSnapshot];
      if (v == null) return '';
      return escapeCsvCell(String(v));
    });
    cells.push(String(computeGrossAssets(s)));
    cells.push(String(computeNetWorth(s)));
    lines.push(cells.join(','));
  }
  return lines.join('\n');
}

export function parseCsvToSnapshots(csvText: string): { snapshots: NetWorthSnapshot[]; errors: string[] } {
  const errors: string[] = [];
  const lines = csvText.split(/\r?\n/).filter((l) => l.trim());
  if (lines.length < 2) {
    errors.push('CSV needs a header row and at least one data row.');
    return { snapshots: [], errors };
  }
  const headerCells = splitCsvLine(lines[0]);
  const colIndex: Record<string, number> = {};
  headerCells.forEach((h, i) => {
    const key = normalizeHeader(h);
    colIndex[key] = i;
  });

  const snapshots: NetWorthSnapshot[] = [];
  for (let li = 1; li < lines.length; li++) {
    const cells = splitCsvLine(lines[li]);
    if (cells.every((c) => !c.trim())) continue;

    const get = (canonical: string): string => {
      const aliases = [canonical, normalizeHeader(canonical)];
      for (const a of aliases) {
        const idx = colIndex[a];
        if (idx != null && cells[idx] != null) return cells[idx];
      }
      return '';
    };

    let dateRaw = get('recordDate');
    if (!dateRaw) {
      for (let i = 0; i < headerCells.length; i++) {
        if (/record|date/i.test(headerCells[i]) && cells[i]?.trim()) {
          dateRaw = cells[i];
          break;
        }
      }
    }
    const recordDate = parseFlexibleDate(dateRaw) || parseFlexibleDate(cells[0] || '') || null;
    if (!recordDate) {
      errors.push(`Row ${li + 1}: could not parse record date from "${dateRaw || cells[0]}"`);
      continue;
    }

    const snap: NetWorthSnapshot = {
      id: newSnapshotId(),
      recordDate,
      outstanding: parseNum(get('outstanding')) ?? tryNumericColumn(cells, headerCells, /outstanding/i),
      savings: parseNum(get('savings')) ?? tryNumericColumn(cells, headerCells, /savings/i),
      equityAmountInvested:
        parseNum(get('equityAmountInvested')) ??
        tryNumericColumn(cells, headerCells, /equity.*invest/i),
      equityCurrentValue:
        parseNum(get('equityCurrentValue')) ??
        tryNumericColumn(cells, headerCells, /equity/i),
      mfAmountInvested: parseNum(get('mfAmountInvested')),
      mfCurrentValue:
        parseNum(get('mfCurrentValue')) ??
        tryNumericColumn(cells, headerCells, /mutual|sip|mf/i),
      corporateFdBonds: parseNum(get('corporateFdBonds')),
      ppf: parseNum(get('ppf')),
      europeIncome: parseNum(get('europeIncome')),
      euSalary: parseNum(get('euSalary')),
      euEquity: parseNum(get('euEquity')),
      euConversionRate: parseNum(get('euConversionRate')),
      salary: parseNum(get('salary')),
      bankFd: parseNum(get('bankFd')),
      cryptoUsdt: parseNum(get('cryptoUsdt')),
      cryptoUsd: parseNum(get('cryptoUsd')),
      cryptoToInrRate: parseNum(get('cryptoToInrRate')),
      realEstate: parseNum(get('realEstate')),
      realEstateDescription: get('realEstateDescription') || undefined,
      goldGrams: parseNum(get('goldGrams')),
      goldInrPerGram: parseNum(get('goldInrPerGram')),
      goldTotalInr: parseNum(get('goldTotalInr')),
      usStockUsd: parseNum(get('usStockUsd')),
      usStockConversionRate: parseNum(get('usStockConversionRate')),
      usStockTotalInr: parseNum(get('usStockTotalInr')),
      usAssetsTotalInr: parseNum(get('usAssetsTotalInr')),
      usdAmount: parseNum(get('usdAmount')),
      usdToInrRate: parseNum(get('usdToInrRate')),
      excelTotalAssets: parseNum(get('excelTotalAssets')) ?? tryNumericColumn(cells, headerCells, /^total$/i),
      customerFdLiability: parseNum(get('customerFdLiability')),
      partnerCapitalLiability: parseNum(get('partnerCapitalLiability')),
      monthlyGrowthRatePct: parseNum(get('monthlyGrowthRatePct')),
      monthlyInterestIncomePossible: parseNum(get('monthlyInterestIncomePossible')),
      monthlyInterestIncome: parseNum(get('monthlyInterestIncome')),
      monthOnMonthChangePct: parseNum(get('monthOnMonthChangePct')),
      description: get('description') || undefined,
      sloganOfTheMonth: get('sloganOfTheMonth') || undefined,
    };
    snapshots.push(snap);
  }

  return { snapshots, errors };
}

function tryNumericColumn(
  cells: string[],
  headers: string[],
  re: RegExp
): number | undefined {
  const idx = headers.findIndex((h) => re.test(h.trim()));
  if (idx < 0) return undefined;
  return parseNum(cells[idx] || '');
}

/** Minimal CSV line splitter (handles quoted fields) */
export function splitCsvLine(line: string): string[] {
  const out: string[] = [];
  let cur = '';
  let inQ = false;
  for (let i = 0; i < line.length; i++) {
    const c = line[i];
    if (inQ) {
      if (c === '"') {
        if (line[i + 1] === '"') {
          cur += '"';
          i++;
        } else {
          inQ = false;
        }
      } else {
        cur += c;
      }
    } else if (c === '"') {
      inQ = true;
    } else if (c === ',') {
      out.push(cur);
      cur = '';
    } else {
      cur += c;
    }
  }
  out.push(cur);
  return out;
}

export function downloadCsv(filename: string, content: string): void {
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}
